import React, {useEffect} from "react";
import "../styles/components/legal.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.info("[INFO] PrivacyPolicy mounted. Full 19-section compliance view.");
      console.debug("[DEBUG] Effective: 20 March 2026. Jurisdictions: India/East Africa.");
    }
  }, []);

  return (
      <div className="shell">
        <div className="page-content">
          <div className="page-hero">
            <h1>Privacy Policy</h1>
            <div className="orange-bar"></div>
            <div className="hero-meta">
              <span>&#128197; Effective: 20 March 2026</span>
              <span>&#128197; Last Updated: 20 March 2026</span>
              <span>&#127758; India &amp; East Africa</span>
              <span>&#127970; Satyology Solutions Private Limited</span>
            </div>
          </div>


          <div className="toc-card">
            <h2>&#9776; &nbsp;Table of Contents</h2>
            <ol>
              <li><a href="#s1">Who We Are</a></li>
              <li><a href="#s2">Scope of This Policy</a></li>
              <li><a href="#s3">What Data We Collect</a></li>
              <li><a href="#s4">How We Collect Data</a></li>
              <li><a href="#s5">Why We Collect Data (Purpose)</a></li>
              <li><a href="#s6">Legal Basis for Processing</a></li>
              <li><a href="#s7">Location &amp; GPS Data (Special Category)</a></li>
              <li><a href="#s8">How We Use Your Data</a></li>
              <li><a href="#s9">Data Sharing &amp; Third Parties</a></li>
              <li><a href="#s10">Data Retention</a></li>
              <li><a href="#s11">Data Security</a></li>
              <li><a href="#s12">Your Rights — India (DPDPA 2023)</a></li>
              <li><a href="#s13">Your Rights — East Africa</a></li>
              <li><a href="#s14">WhatsApp &amp; Notification Data</a></li>
              <li><a href="#s15">Cookies &amp; Tracking Technologies</a></li>
              <li><a href="#s16">Children's Privacy</a></li>
              <li><a href="#s17">Cross-Border Data Transfers</a></li>
              <li><a href="#s18">Changes to This Policy</a></li>
              <li><a href="#s19">Grievance &amp; Contact</a></li>
            </ol>
          </div>


          <div className="section-card" id="s1">
            <h2><span className="section-num">1</span> Who We Are</h2>
            <div className="orange-bar"></div>
            <p>This Privacy Policy is published by <strong>Satyology Solutions Private Limited</strong>, the company behind the <strong>Yekkada – Get Tracking!</strong> platform.</p>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Detail</th><th>Information</th></tr></thead>
                <tbody>
                <tr><td><strong>Legal Name</strong></td><td>Satyology Solutions Private Limited</td></tr>
                <tr><td><strong>Brand</strong></td><td>Yekkada – Get Tracking!</td></tr>
                <tr><td><strong>Corporate Office</strong></td><td>#A604, Oceanus Monarda, Kasavanahalli Main Road, Amurtha College Road, Bangalore – 560035 (KA), INDIA</td></tr>
                <tr><td><strong>Registered Office</strong></td><td>Dr. No. 22C-10-46, Badeti Street, Power Pet, Eluru – 534002 (AP), INDIA</td></tr>
                <tr><td><strong>CIN</strong></td><td>U72200AP2015PTC095939</td></tr>
                <tr><td><strong>GST No.</strong></td><td>37AAVCS3027M1ZO</td></tr>
                <tr><td><strong>Corporate Website</strong></td><td><a href="https://www.satyology.com" target="_blank" rel="noopener noreferrer">www.satyology.com</a></td></tr>
                <tr><td><strong>Platform</strong></td><td><a href="https://yekkada.com" target="_blank" rel="noopener noreferrer">yekkada.com</a></td></tr>
                <tr><td><strong>Privacy Contact</strong></td><td><a href="mailto:privacy@yekkada.com">privacy@yekkada.com</a></td></tr>
                </tbody>
              </table>
            </div>
            <p>For the purposes of data protection law: in India, Satyology Solutions Private Limited is the <strong>Data Fiduciary</strong> (DPDPA 2023); in East Africa, it acts as the <strong>Data Controller and Data Processor</strong> under applicable national legislation.</p>
          </div>


          <div className="section-card" id="s2">
            <h2><span className="section-num">2</span> Scope of This Policy</h2>
            <div className="orange-bar"></div>
            <p>This Privacy Policy applies to all personal data collected and processed through:</p>
            <ul>
              <li><strong>Yekkada Web Platform</strong> — yekkada.com</li>
              <li><strong>Yekkada Mobile APK</strong> — Android application</li>
              <li><strong>IoT Devices</strong> — GPS trackers, smart locks, BT locks, RFID tags connected to the Platform</li>
              <li><strong>API Integrations</strong> — third-party systems connected via Yekkada's developer API</li>
              <li><strong>WhatsApp / SMS Notifications</strong> — automated alerts sent to registered users</li>
              <li><strong>Support Communications</strong> — email, WhatsApp, or in-app support channels</li>
            </ul>
            <p>This Policy applies to all users across <strong>India</strong> and the <strong>East African region</strong> (Kenya, Uganda, Tanzania, Rwanda, Ethiopia, and other EAC member states).</p>
            <div className="alert-box">&#9989; <strong>B2B Platform:</strong> Yekkada is a business-to-business (B2B) platform. The primary data subjects are business owners, fleet managers, field supervisors, and registered business users — not end consumers.</div>
          </div>


          <div className="section-card" id="s3">
            <h2><span className="section-num">3</span> What Data We Collect</h2>
            <div className="orange-bar"></div>
            <h3>3.1 Account &amp; Identity Data</h3>
            <ul>
              <li>Full name, business name, designation</li>
              <li>Email address, mobile number</li>
              <li>Business address, GST/KRA PIN number</li>
              <li>Login credentials (stored encrypted)</li>
              <li>Profile photo (optional)</li>
            </ul>
            <h3>3.2 Asset &amp; Device Data</h3>
            <ul>
              <li>Asset names, IDs, and descriptions</li>
              <li>Device IMEI / serial numbers (GPS trackers, BT locks)</li>
              <li>Device status — locked, unlocked, online, offline</li>
              <li>Battery level, charging status, speed readings</li>
              <li>RFID tag IDs and scan events</li>
            </ul>
            <h3>3.3 Location &amp; GPS Data <span className="badge badge-orange">High-Risk</span></h3>
            <ul>
              <li>Real-time GPS coordinates of tracked assets</li>
              <li>Historical location trails and route data</li>
              <li>Geofence zone definitions and breach events</li>
              <li>Last known location and timestamps</li>
            </ul>
            <h3>3.4 Field Operations Data</h3>
            <ul>
              <li>Field staff names and contact numbers (added by business customers)</li>
              <li>Check-in / check-out timestamps and locations</li>
              <li>Task assignments and completion records</li>
              <li>Attendance and operational reports</li>
            </ul>
            <h3>3.5 Technical &amp; Usage Data</h3>
            <ul>
              <li>IP address, browser type, operating system</li>
              <li>Device type, app version</li>
              <li>Pages visited, features used, session duration</li>
              <li>API call logs and integration activity</li>
              <li>Error logs and crash reports</li>
            </ul>
            <h3>3.6 Communication Data</h3>
            <ul>
              <li>WhatsApp message delivery status (sent, delivered, read)</li>
              <li>SMS delivery receipts</li>
              <li>Support ticket content and history</li>
              <li>Opt-in / opt-out preferences and timestamps</li>
            </ul>
            <h3>3.7 Payment Data</h3>
            <ul>
              <li>Subscription plan selected</li>
              <li>Payment confirmation reference numbers</li>
              <li>GST invoice details</li>
              <li><strong>Note:</strong> Full card/bank details are NOT stored by Yekkada — processed by PCI-DSS compliant payment gateways only</li>
            </ul>
          </div>


          <div className="section-card" id="s4">
            <h2><span className="section-num">4</span> How We Collect Data</h2>
            <div className="orange-bar"></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Collection Method</th><th>What Is Collected</th></tr></thead>
                <tbody>
                <tr><td><strong>Account Registration</strong></td><td>Identity, business, and contact data provided by the user</td></tr>
                <tr><td><strong>IoT Device Telemetry</strong></td><td>GPS coordinates, lock/unlock events, battery, speed — automatically from connected devices</td></tr>
                <tr><td><strong>Yekkada APK</strong></td><td>Device permissions granted (location, camera for QR, notifications) — only when explicitly permitted by user</td></tr>
                <tr><td><strong>Web Platform Usage</strong></td><td>Browser cookies, session data, feature usage logs</td></tr>
                <tr><td><strong>Business Customer Input</strong></td><td>Asset names, field staff details, geofence zones entered by the business admin</td></tr>
                <tr><td><strong>WhatsApp / SMS</strong></td><td>Delivery status callbacks from BSP (Gupshup)</td></tr>
                <tr><td><strong>Support Channels</strong></td><td>Information voluntarily provided during support interactions</td></tr>
                <tr><td><strong>Third-Party APIs</strong></td><td>Data received from integrated business systems (ERP, WMS)</td></tr>
                </tbody>
              </table>
            </div>
          </div>


          <div className="section-card" id="s5">
            <h2><span className="section-num">5</span> Why We Collect Data (Purpose Limitation)</h2>
            <div className="orange-bar"></div>
            <div className="info-box">&#128274; <strong>Purpose Limitation Principle:</strong> Under DPDPA 2023 and East African data protection laws, we collect only what is strictly necessary for the stated purpose. Data collected for one purpose is not reused for an incompatible purpose without fresh consent.</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Purpose</th><th>Data Used</th><th>Legal Basis</th></tr></thead>
                <tbody>
                <tr><td>Platform account creation &amp; authentication</td><td>Name, email, phone, password</td><td>Contractual necessity</td></tr>
                <tr><td>Real-time asset tracking &amp; visibility</td><td>GPS data, device telemetry</td><td>Contractual necessity + Consent</td></tr>
                <tr><td>Lock / unlock event notifications</td><td>Device status, location, timestamp</td><td>Contractual necessity</td></tr>
                <tr><td>Geofence breach alerts</td><td>GPS coordinates, zone definitions</td><td>Contractual necessity</td></tr>
                <tr><td>Field operations management</td><td>Staff check-in, location, task data</td><td>Consent + Legitimate interest</td></tr>
                <tr><td>WhatsApp / SMS / push notifications</td><td>Phone number, message content, delivery status</td><td>Explicit consent (opt-in)</td></tr>
                <tr><td>Billing &amp; subscription management</td><td>Business details, payment reference</td><td>Contractual necessity</td></tr>
                <tr><td>Platform performance &amp; analytics</td><td>Usage logs, technical data</td><td>Legitimate interest</td></tr>
                <tr><td>Security, fraud prevention &amp; compliance</td><td>IP address, access logs</td><td>Legal obligation + Legitimate interest</td></tr>
                <tr><td>Customer support</td><td>Communication history</td><td>Contractual necessity + Consent</td></tr>
                </tbody>
              </table>
            </div>
          </div>


          <div className="section-card" id="s6">
            <h2><span className="section-num">6</span> Legal Basis for Processing <span className="badge badge-both">Both Regions</span></h2>
            <div className="orange-bar"></div>
            <p>We rely on the following lawful bases for processing personal data:</p>
            <ul>
              <li><strong>Consent:</strong> Where you have given explicit, informed, and freely-given consent (e.g., WhatsApp opt-in, location permissions in the APK). You may withdraw consent at any time without affecting lawfulness of prior processing.</li>
              <li><strong>Contractual Necessity:</strong> Processing required to deliver the Yekkada platform services you have subscribed to.</li>
              <li><strong>Legitimate Interests:</strong> Processing for platform security, fraud prevention, analytics, and service improvement — where these interests are not overridden by your fundamental rights.</li>
              <li><strong>Legal Obligation:</strong> Processing required to comply with applicable laws in India and East African jurisdictions.</li>
            </ul>
          </div>


          <div className="section-card" id="s7">
            <h2><span className="section-num">7</span> Location &amp; GPS Data <span className="badge badge-orange">High-Risk Personal Data</span></h2>
            <div className="orange-bar"></div>
            <div className="info-box">&#128205; <strong>Special Category — High Risk:</strong> Under India's DPDPA 2023 and East African data protection laws, location and GPS data is treated as <strong>high-risk personal data</strong> due to its ability to reveal sensitive behavioural patterns, routines, and movements of individuals. Enhanced protections apply.</div>
            <h3>7.1 What GPS Data We Collect</h3>
            <p>Yekkada collects real-time and historical GPS coordinates of <strong>assets</strong> (vehicles, equipment, devices) registered by business customers — not of individual persons directly. However, where an asset is assigned to a specific driver or field employee, the location data is indirectly linked to that individual.</p>
            <h3>7.2 Consent and Disclosure to Field Staff</h3>
            <p>Business customers using Yekkada to track vehicles or field staff are responsible for:</p>
            <ul>
              <li>Informing all tracked individuals that their vehicle/device location is being monitored</li>
              <li>Obtaining explicit consent where required under applicable employment and data protection laws</li>
              <li>Defining and communicating the purpose and duration of tracking</li>
              <li>Ensuring tracking is limited to working hours/operational use where legally required</li>
            </ul>
            <h3>7.3 Data Minimisation</h3>
            <p>GPS data is collected <strong>only while the IoT device is active and assigned</strong>. Historical GPS trails are retained for the period defined in the customer's subscription plan and are permanently deleted thereafter.</p>
            <h3>7.4 Access Controls</h3>
            <p>GPS location data is accessible only to the registered business admin and authorised users within their Yekkada account. Yekkada staff access location data only for technical support and only with documented authorisation.</p>
          </div>


          <div className="section-card" id="s8">
            <h2><span className="section-num">8</span> How We Use Your Data</h2>
            <div className="orange-bar"></div>
            <p>8.1 <strong>Service Delivery:</strong> Providing real-time asset tracking, lock/unlock management, field operations monitoring, alerts, and dashboard reporting as subscribed.</p>
            <p>8.2 <strong>Notifications:</strong> Sending time-critical operational alerts (device status changes, geofence breaches, security events) via WhatsApp, SMS, and in-app push notifications to opted-in users.</p>
            <p>8.3 <strong>Account Management:</strong> Managing your subscription, billing, user access, and platform configuration.</p>
            <p>8.4 <strong>Platform Improvement:</strong> Analysing anonymised usage patterns to improve platform features, performance, and user experience.</p>
            <p>8.5 <strong>Security &amp; Fraud Prevention:</strong> Detecting and preventing unauthorised access, abuse, or fraudulent use of the Platform.</p>
            <p>8.6 <strong>Legal Compliance:</strong> Meeting our obligations under applicable Indian and East African laws, including responding to lawful government requests.</p>
            <p>8.7 <strong>What We Do NOT Do:</strong></p>
            <ul>
              <li>We do not sell your personal data to third parties</li>
              <li>We do not use your data for targeted advertising</li>
              <li>We do not share location data with any party not authorised by you</li>
              <li>We do not use your data to build individual behavioural profiles for commercial purposes</li>
            </ul>
          </div>


          <div className="section-card" id="s9">
            <h2><span className="section-num">9</span> Data Sharing and Third Parties</h2>
            <div className="orange-bar"></div>
            <div className="alert-box">&#9989; <strong>We do not sell your data.</strong> Yekkada shares personal data only with trusted sub-processors necessary to deliver our services, and only under strict data processing agreements.</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Third Party / Category</th><th>Purpose</th><th>Data Shared</th><th>Region</th></tr></thead>
                <tbody>
                <tr><td><strong>Gupshup (WhatsApp BSP)</strong></td><td>Delivering WhatsApp notifications</td><td>Phone number, message content</td><td>India &amp; East Africa</td></tr>
                <tr><td><strong>MSG91</strong></td><td>SMS fallback notifications</td><td>Phone number, alert text</td><td>India</td></tr>
                <tr><td><strong>Africa's Talking</strong></td><td>SMS notifications</td><td>Phone number, alert text</td><td>East Africa</td></tr>
                <tr><td><strong>Payment Gateways</strong></td><td>Subscription billing</td><td>Business name, invoice details</td><td>Both</td></tr>
                <tr><td><strong>Cloud Infrastructure</strong></td><td>Hosting, storage, database</td><td>All platform data (encrypted)</td><td>Both</td></tr>
                <tr><td><strong>Mapping Providers</strong></td><td>Displaying location on map</td><td>GPS coordinates (anonymised)</td><td>Both</td></tr>
                <tr><td><strong>Analytics Tools</strong></td><td>Platform usage analytics</td><td>Anonymised usage data only</td><td>Both</td></tr>
                <tr><td><strong>Legal / Regulatory Authorities</strong></td><td>Compliance with lawful orders</td><td>As legally required</td><td>Both</td></tr>
                </tbody>
              </table>
            </div>
            <p>All third-party sub-processors are bound by contractual data processing agreements that require them to process data only for specified purposes, maintain adequate security standards, and comply with applicable data protection laws.</p>
          </div>


          <div className="section-card" id="s10">
            <h2><span className="section-num">10</span> Data Retention</h2>
            <div className="orange-bar"></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Data Type</th><th>Retention Period</th><th>Reason</th></tr></thead>
                <tbody>
                <tr><td>Account &amp; identity data</td><td>Duration of subscription + 90 days post-termination</td><td>Service delivery &amp; legal obligation</td></tr>
                <tr><td>Real-time GPS / location data</td><td>Per subscription plan (typically 30–365 days rolling)</td><td>Operational history &amp; reporting</td></tr>
                <tr><td>Device event logs (lock/unlock)</td><td>Per subscription plan (typically 30–365 days rolling)</td><td>Audit trail &amp; customer reporting</td></tr>
                <tr><td>Field operations records</td><td>Duration of subscription + 90 days</td><td>Customer reporting &amp; compliance</td></tr>
                <tr><td>Payment &amp; billing records</td><td>7 years</td><td>Indian tax / accounting law (GST)</td></tr>
                <tr><td>Support communications</td><td>2 years from last interaction</td><td>Service continuity</td></tr>
                <tr><td>WhatsApp notification logs</td><td>90 days</td><td>Delivery verification</td></tr>
                <tr><td>Technical / access logs</td><td>180 days</td><td>Security monitoring</td></tr>
                </tbody>
              </table>
            </div>
            <p>Upon expiry of the retention period or account termination (whichever is earlier), personal data is <strong>securely deleted or anonymised</strong> using industry-standard data destruction methods.</p>
          </div>


          <div className="section-card" id="s11">
            <h2><span className="section-num">11</span> Data Security</h2>
            <div className="orange-bar"></div>
            <p>Yekkada implements a multi-layered security architecture to protect your personal data:</p>
            <h3>Technical Measures</h3>
            <ul>
              <li><strong>Encryption in transit:</strong> All data transmitted between your device and Yekkada's servers is encrypted using TLS 1.2+ (HTTPS)</li>
              <li><strong>Encryption at rest:</strong> Sensitive data fields (passwords, personal identifiers) are encrypted in the database using AES-256</li>
              <li><strong>Access controls:</strong> Role-based access controls (RBAC) — users see only data relevant to their authorised role</li>
              <li><strong>Authentication:</strong> Secure login with hashed passwords; optional two-factor authentication (2FA)</li>
              <li><strong>API security:</strong> API keys and OAuth tokens for all third-party integrations</li>
            </ul>
            <h3>Organisational Measures</h3>
            <ul>
              <li>Designated Data Protection Officer (DPO) responsible for privacy compliance</li>
              <li>Staff trained on data protection principles and handling procedures</li>
              <li>Access to personal data limited to authorised personnel on a need-to-know basis</li>
              <li>Regular security reviews and vulnerability assessments</li>
              <li>Data breach response plan with defined notification procedures</li>
            </ul>
            <div className="info-box">&#9888; <strong>Breach Notification:</strong> In the event of a personal data breach, Yekkada will notify the relevant data protection authority (India: Data Protection Board; East Africa: applicable national authority) and affected individuals within the legally required timeframes — 72 hours for East Africa; as prescribed by DPDPA 2023 for India.</div>
          </div>


          <div className="section-card" id="s12">
            <h2><span className="section-num">12</span> Your Rights — India <span className="badge badge-india">DPDPA 2023</span></h2>
            <div className="orange-bar"></div>
            <div className="info-box"><strong>Applicable Law:</strong> Digital Personal Data Protection Act, 2023 (DPDPA) — effective November 2025 per MeitY notification.</div>
            <p>As a Data Principal (individual whose data is processed) under the DPDPA 2023, you have the following rights:</p>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Right</th><th>What It Means</th><th>How to Exercise</th></tr></thead>
                <tbody>
                <tr><td><strong>Right to Access</strong></td><td>Obtain a summary of personal data Yekkada holds about you and how it is processed</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Correction</strong></td><td>Correct inaccurate or incomplete personal data</td><td>Via dashboard settings or email</td></tr>
                <tr><td><strong>Right to Erasure</strong></td><td>Request deletion of personal data when purpose is fulfilled or consent is withdrawn</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Withdraw Consent</strong></td><td>Withdraw consent for any processing based on consent, at any time</td><td>Dashboard settings or reply STOP to WhatsApp</td></tr>
                <tr><td><strong>Right to Grievance Redressal</strong></td><td>Raise a complaint with Yekkada's DPO; escalate to Data Protection Board if unresolved</td><td>dpo@yekkada.com</td></tr>
                <tr><td><strong>Right to Nominate</strong></td><td>Nominate a person to exercise your rights in the event of death or incapacity</td><td>Email privacy@yekkada.com</td></tr>
                </tbody>
              </table>
            </div>
            <p>We will respond to all valid requests within <strong>30 days</strong> of receipt. For complex requests, this may be extended by a further 30 days with prior notice.</p>
            <p><strong>Escalation:</strong> If you are not satisfied with our response, you may file a complaint with the <strong>Data Protection Board of India</strong> at <a href="https://www.meity.gov.in" target="_blank" rel="noopener noreferrer">www.meity.gov.in</a>.</p>
          </div>


          <div className="section-card" id="s13">
            <h2><span className="section-num">13</span> Your Rights — East Africa <span className="badge badge-ea">East Africa</span></h2>
            <div className="orange-bar"></div>
            <div className="info-box"><strong>Applicable Laws:</strong> Kenya DPA 2019 | Uganda Data Protection &amp; Privacy Act 2019 | Tanzania Personal Data Protection Act 2022 | Rwanda Law No. 058/2021 | and equivalent EAC jurisdiction legislation.</div>
            <p>As a data subject in the East African region, you have the following rights:</p>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Right</th><th>What It Means</th><th>How to Exercise</th></tr></thead>
                <tbody>
                <tr><td><strong>Right to be Informed</strong></td><td>Know what data is collected, why, and how it is used — this Privacy Policy fulfils this obligation</td><td>This document</td></tr>
                <tr><td><strong>Right of Access</strong></td><td>Request a copy of your personal data held by Yekkada</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Rectification</strong></td><td>Correct inaccurate or outdated personal data</td><td>Dashboard settings or email</td></tr>
                <tr><td><strong>Right to Erasure</strong></td><td>Request deletion of your personal data</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Restriction</strong></td><td>Request limitation of how your data is processed while a dispute is pending</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Data Portability</strong></td><td>Receive your data in a structured, machine-readable format</td><td>Email privacy@yekkada.com</td></tr>
                <tr><td><strong>Right to Object</strong></td><td>Object to processing based on legitimate interests</td><td>Email privacy@yekkada.com</td></tr>
                </tbody>
              </table>
            </div>
            <p>We will respond to all valid requests within <strong>30 days</strong>.</p>
            <p><strong>Escalation by Country:</strong></p>
            <ul>
              <li><strong>Kenya:</strong> Office of the Data Protection Commissioner (ODPC) — <a href="https://www.odpc.go.ke" target="_blank" rel="noopener noreferrer">www.odpc.go.ke</a></li>
              <li><strong>Uganda:</strong> Personal Data Protection Office (PDPO)</li>
              <li><strong>Tanzania:</strong> Personal Data Protection Commission (PDPC)</li>
              <li><strong>Rwanda:</strong> Rwanda Information Society Authority (RISA) — <a href="https://www.risa.rw" target="_blank" rel="noopener noreferrer">www.risa.rw</a></li>
            </ul>
          </div>


          <div className="section-card" id="s14">
            <h2><span className="section-num">14</span> WhatsApp &amp; Notification Data <span className="badge badge-both">Both Regions</span></h2>
            <div className="orange-bar"></div>
            <p>14.1 <strong>Opt-In Requirement:</strong> Yekkada sends WhatsApp notifications only to users who have <strong>explicitly opted in</strong> during platform registration. The opt-in is recorded with a timestamp and customer ID in our database.</p>
            <p>14.2 <strong>Types of Notifications Sent:</strong></p>
            <ul>
              <li>Device status alerts (lock/unlock events, online/offline status)</li>
              <li>Geofence breach notifications</li>
              <li>Field staff operational updates</li>
              <li>Account and subscription reminders</li>
              <li>Customer support responses</li>
            </ul>
            <p>14.3 <strong>Data Shared with WhatsApp BSP (Gupshup):</strong> Your registered phone number and the notification message content are transmitted to our authorised WhatsApp Business Solution Provider (Gupshup) to deliver notifications. Gupshup processes this data as a sub-processor under a data processing agreement and in compliance with Meta's Business Policy.</p>
            <p>14.4 <strong>Opt-Out:</strong> You may opt out at any time by: replying <strong>"STOP"</strong> to any WhatsApp message from Yekkada; updating notification preferences in your dashboard; or emailing <a href="mailto:support@yekkada.com">support@yekkada.com</a>.</p>
            <p>14.5 <strong>Message Retention:</strong> WhatsApp notification delivery logs are retained for 90 days for delivery verification and then permanently deleted.</p>
          </div>


          <div className="section-card" id="s15">
            <h2><span className="section-num">15</span> Cookies &amp; Tracking Technologies</h2>
            <div className="orange-bar"></div>
            <p>The Yekkada web platform uses the following types of cookies and tracking technologies:</p>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Cookie Type</th><th>Purpose</th><th>Duration</th><th>Can Be Disabled?</th></tr></thead>
                <tbody>
                <tr><td><strong>Essential / Session</strong></td><td>Login authentication, session management — platform cannot function without these</td><td>Session</td><td>No (required)</td></tr>
                <tr><td><strong>Functional</strong></td><td>Remembering user preferences (language, dashboard layout, filters)</td><td>30 days</td><td>Yes</td></tr>
                <tr><td><strong>Analytics</strong></td><td>Anonymised platform usage statistics to improve performance</td><td>90 days</td><td>Yes</td></tr>
                <tr><td><strong>Security</strong></td><td>CSRF protection, bot detection</td><td>Session</td><td>No (required)</td></tr>
                </tbody>
              </table>
            </div>
            <p>Yekkada does <strong>not</strong> use advertising cookies, third-party tracking pixels, or social media tracking on its platform pages.</p>
            <p>You can manage cookie preferences through your browser settings. Disabling essential cookies will impair platform functionality.</p>
          </div>


          <div className="section-card" id="s16">
            <h2><span className="section-num">16</span> Children's Privacy</h2>
            <div className="orange-bar"></div>
            <p>16.1 Yekkada is a <strong>B2B platform intended solely for business use</strong>. We do not knowingly collect personal data from individuals under the age of <strong>18 years</strong>.</p>
            <p>16.2 Under India's DPDPA 2023, processing of personal data of children (under 18) requires verifiable parental consent and is prohibited from tracking or behavioural monitoring purposes.</p>
            <p>16.3 If Yekkada becomes aware that personal data of a minor has been collected, we will immediately delete such data and notify the account holder.</p>
            <p>16.4 If you believe a minor's data has been inadvertently collected through the Platform, please contact <a href="mailto:privacy@yekkada.com">privacy@yekkada.com</a> immediately.</p>
          </div>


          <div className="section-card" id="s17">
            <h2><span className="section-num">17</span> Cross-Border Data Transfers</h2>
            <div className="orange-bar"></div>
            <p>17.1 <strong>India:</strong> Personal data of Indian users is stored and processed on servers <strong>located within India</strong> in compliance with DPDPA 2023 data localisation principles. Any cross-border transfer occurs only where permitted by law and with adequate contractual safeguards in place.</p>
            <p>17.2 <strong>East Africa:</strong> Where personal data of East African data subjects is transferred outside their country of residence (e.g., to cloud infrastructure providers), Yekkada ensures:</p>
            <ul>
              <li>The receiving country offers adequate data protection standards, or</li>
              <li>Appropriate contractual safeguards (standard contractual clauses / data transfer agreements) are in place, or</li>
              <li>The transfer is necessary for the performance of the service contract</li>
            </ul>
            <p>17.3 Cross-border transfers are documented and available for review by relevant data protection authorities upon request.</p>
          </div>


          <div className="section-card" id="s18">
            <h2><span className="section-num">18</span> Changes to This Policy</h2>
            <div className="orange-bar"></div>
            <p>18.1 Yekkada reserves the right to update this Privacy Policy at any time to reflect changes in our services, data practices, or applicable laws.</p>
            <p>18.2 When material changes are made, we will notify you at least <strong>30 days in advance</strong> via:</p>
            <ul>
              <li>Email to your registered email address</li>
              <li>WhatsApp notification (if opted in)</li>
              <li>In-platform banner notification on the Yekkada dashboard</li>
            </ul>
            <p>18.3 The "Last Updated" date at the top of this page will always reflect the most recent revision.</p>
            <p>18.4 Continued use of the Platform after the effective date of changes constitutes your acceptance of the revised Privacy Policy.</p>
          </div>


          <div className="section-card" id="s19">
            <h2><span className="section-num">19</span> Grievance, DPO &amp; Contact</h2>
            <div className="orange-bar"></div>
            <p>If you have any questions, concerns, or grievances regarding this Privacy Policy or our data handling practices, please contact us:</p>
            <div className="contact-grid">
              <div className="contact-box">
                <div className="cb-title">&#127968; Company Details</div>
                <p><strong>Satyology Solutions Private Limited</strong></p>
                <p>Brand: Yekkada – Get Tracking!</p>
                <p><strong>Corp. Office:</strong> #A604, Oceanus Monarda, Kasavanahalli Main Road, Amurtha College Road, Bangalore – 560035 (KA), INDIA</p>
                <p><strong>Regd. Office:</strong> Dr. No. 22C-10-46, Badeti Street, Power Pet, Eluru – 534002 (AP), INDIA</p>
                <p><strong>CIN:</strong> U72200AP2015PTC095939</p>
                <p><strong>GST No.:</strong> 37AAVCS3027M1ZO</p>
              </div>
              <div className="contact-box">
                <div className="cb-title">&#128231; Privacy Contacts</div>
                <p><strong>General Privacy Queries:</strong><br/><a href="mailto:privacy@yekkada.com">privacy@yekkada.com</a></p>
                <p><strong>Data Protection Officer (DPO):</strong><br/><a href="mailto:dpo@yekkada.com">dpo@yekkada.com</a></p>
                <p><strong>General Support:</strong><br/><a href="mailto:support@yekkada.com">support@yekkada.com</a></p>
                <p><strong>Website:</strong><br/><a href="https://yekkada.com" target="_blank" rel="noopener noreferrer">yekkada.com</a></p>
                <p><strong>Response Time:</strong> Within 30 days of receipt</p>
              </div>
            </div>
            <div className="alert-box" style={{ marginTop: "16px" }}>
              &#9654; <strong>Unresolved Complaints — India:</strong> Data Protection Board of India via <a href="https://www.meity.gov.in" target="_blank" rel="noopener noreferrer">www.meity.gov.in</a><br/>
              &#9654; <strong>Unresolved Complaints — Kenya:</strong> ODPC via <a href="https://www.odpc.go.ke" target="_blank" rel="noopener noreferrer">www.odpc.go.ke</a><br/>
              &#9654; <strong>Unresolved Complaints — Rwanda:</strong> RISA via <a href="https://www.risa.rw" target="_blank" rel="noopener noreferrer">www.risa.rw</a>
            </div>
          </div>

        </div>
      </div>
  );
};

export default PrivacyPolicy;